import {
	BasicButton,
	FloatingLoader,
	RadioCard,
	SecondCustomInput,
	Typography,
} from "@/components";
import { useDebounce } from "@/hooks/useDebounce";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { QUERY_KEYS } from "@/libraries/constant";
import { fromYearToAge } from "@/libraries/date";
import { getCasesQueryOptions } from "@/queries";
import {
	type CaseReferralFormFieldSchema,
	CaseSearchParamSchema,
} from "@/schemas";
import SearchIcon from "@mui/icons-material/Search";
import { onlineManager, useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";

export const Route = createFileRoute("/dashboard/case/referrals/")({
	validateSearch: zodSearchValidator(CaseSearchParamSchema),
	loaderDeps: () => ({ status: "active" as const }),
	loader: ({ context, deps }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return context.queryClient.ensureInfiniteQueryData(
				getCasesQueryOptions(deps),
			);
		}
		return context.queryClient.getQueryData([QUERY_KEYS.GET_CASES, deps]);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const searchQ = Route.useSearch({
		select: (s) => s.q ?? "",
	});
	const deps = Route.useLoaderDeps({
		select: (s) => s,
	});
	const scrollRef = useRef<HTMLDivElement>(null);
	const debouncedSearch = useDebounce(searchQ, 300);

	const { register, watch, setValue } =
		useFormContext<CaseReferralFormFieldSchema>();
	const caseId = watch("caseId");

	const registerField = register("caseId", {
		valueAsNumber: true,
	});

	const {
		data: cases,
		isLoading,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
	} = useSuspenseInfiniteQuery(getCasesQueryOptions(deps));

	const caseItems = useMemo(() => {
		if (!cases.length) return [];
		const filteredCases = cases.filter(
			(caseItem) => !caseItem.member_type.startsWith("external_"),
		);
		if (debouncedSearch) {
			return filteredCases.filter(
				(caseItem) =>
					caseItem.title
						.toLowerCase()
						.includes(debouncedSearch.toLowerCase()) ||
					caseItem.summary
						?.toLowerCase()
						?.includes(debouncedSearch.toLowerCase()) ||
					caseItem.patient?.name
						?.toLowerCase()
						?.includes(debouncedSearch.toLowerCase()) ||
					caseItem.patient?.surname
						?.toLowerCase()
						?.includes(debouncedSearch.toLowerCase()),
			);
		}
		return filteredCases;
	}, [cases, debouncedSearch]);

	const casesVirtualizer = useVirtualizer({
		count: caseItems.length,
		gap: 12,
		overscan: 5,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => 125,
		measureElement: (el) => el.getBoundingClientRect().height,
	});
	const casesVirtualizerItems = casesVirtualizer.getVirtualItems();

	useEffect(() => {
		const [lastItem] = [...casesVirtualizerItems].reverse();

		if (!lastItem) return;

		if (
			lastItem.index >= cases.length - 1 &&
			hasNextPage &&
			!isFetchingNextPage
		) {
			fetchNextPage();
		}
	}, [
		cases.length,
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
		casesVirtualizerItems,
	]);

	useLockBodyScroll();

	return (
		<>
			<div className="container flex flex-col gap-4">
				<div className="flex flex-col gap-1">
					<Typography fontWeight={900} fontSize={14}>
						Select one of your active cases:
					</Typography>
					<Typography fontWeight={400} fontSize={12}>
						Choose the case you need external collaboration for.
					</Typography>
				</div>
				<SecondCustomInput
					type="search"
					name="search"
					placeholder="Search"
					startIcon={<SearchIcon />}
					value={searchQ}
					onChange={(e) =>
						navigate({
							search: {
								q: e.target.value || undefined,
							},
							replace: true,
						})
					}
				/>
			</div>
			<div ref={scrollRef} className="flex-1 relative overflow-y-auto">
				<FloatingLoader open={isLoading || isFetchingNextPage} />
				{casesVirtualizerItems.length ? (
					<div
						className="relative w-full"
						style={{
							height: casesVirtualizer.getTotalSize(),
						}}
					>
						{casesVirtualizerItems.map((casesVirtualizerItem) => {
							const caseItem = caseItems[casesVirtualizerItem.index];
							if (!caseItem) return null;
							return (
								<div
									key={casesVirtualizerItem.key}
									data-index={casesVirtualizerItem.index}
									ref={casesVirtualizer.measureElement}
									className="container absolute top-0 left-0"
									style={{
										transform: `translateY(${casesVirtualizerItem.start}px)`,
									}}
								>
									<RadioCard
										{...registerField}
										title={
											!caseItem.patient
												? caseItem.title
												: [
														caseItem.patient.name,
														caseItem.patient.surname,
													].join(" ")
										}
										info={[
											fromYearToAge(
												!caseItem.patient?.dob
													? caseItem.year_of_birth
													: caseItem.patient.dob,
												" yo",
											),
											!caseItem.patient ? caseItem.summary : caseItem.title,
										].join(" • ")}
										value={caseItem.id}
										checked={+caseId === caseItem.id}
										inputProps={{
											name: registerField.name,
											onChange: (e) => {
												registerField.onChange(e);
												setValue("channelName", caseItem.channel_name, {
													shouldDirty: true,
													shouldTouch: true,
												});
												setValue("_case", caseItem, {
													shouldDirty: true,
													shouldTouch: true,
												});
											},
											onBlur: registerField.onBlur,
										}}
									/>
								</div>
							);
						})}
					</div>
				) : (
					<Typography fontWeight={700} className="text-gray-200" align="center">
						No cases added yet
					</Typography>
				)}
			</div>
			{caseId ? (
				<div className="container bg-white pb-4">
					<Link to="/dashboard/case/referrals/type">
						<BasicButton
							variant="contained"
							color="secondary"
							className="!h-12 text-sm font-bold !shadow-none"
							fullWidth
						>
							Next
						</BasicButton>
					</Link>
				</div>
			) : null}
		</>
	);
}
